export const THEME_STORAGE_KEY = 'nextcompli.theme.overrides';
export const THEME_CUSTOM_PRESETS_STORAGE_KEY = 'nextcompli.theme.customPresets';
export const WEBSITE_LOGO_STORAGE_KEY = 'nextcompli.website.logo.v2';
const LEGACY_WEBSITE_LOGO_STORAGE_KEY = 'nextcompli.website.logo';
export const DEFAULT_WEBSITE_LOGO = '/branding/nextcompli-logo-wide-n.png?v=20260813n';

/** True for the shipped raster lockup. Chrome draws the live mark instead of that file. */
export const isBuiltInWebsiteLogo = (logoPath: string): boolean => {
  const bare = (logoPath || '').trim().split('?')[0].split('#')[0];
  if (!bare) {
    return true;
  }
  return bare.includes('/branding/nextcompli-logo');
};
const LOCAL_BRAND_OVERRIDE_QUERY_PARAM = 'allowLocalBrandOverrides';

export type ThemePreset = {
  id: string;
  label: string;
  overrides: ThemeOverrides;
};

export type ThemeOverrides = {
  '--color-canvas'?: string;
  '--color-canvas-alt'?: string;
  '--color-surface'?: string;
  '--color-surface-muted'?: string;
  '--color-border'?: string;
  '--color-border-strong'?: string;
  '--color-primary'?: string;
  '--color-primary-strong'?: string;
  '--color-primary-soft'?: string;
  '--color-accent'?: string;
  '--color-success'?: string;
  '--color-danger'?: string;
  '--color-text-primary'?: string;
  '--color-text-strong'?: string;
  '--color-text-muted'?: string;
  '--font-sans'?: string;
  '--radius-sm'?: string;
  '--radius-md'?: string;
  '--radius-lg'?: string;
};

export const DEFAULT_THEME: Required<ThemeOverrides> = {
  '--color-canvas': '#f2f6fb',
  '--color-canvas-alt': '#e9f0f9',
  '--color-surface': '#ffffff',
  '--color-surface-muted': '#f7faff',
  '--color-border': '#d8e2f0',
  '--color-border-strong': '#c9d5e5',
  '--color-primary': '#1a73e8',
  '--color-primary-strong': '#0f4fad',
  '--color-primary-soft': '#e8f0ff',
  '--color-accent': '#6d53f6',
  '--color-success': '#2f8f4e',
  '--color-danger': '#b42318',
  '--color-text-primary': '#202124',
  '--color-text-strong': '#1f3654',
  '--color-text-muted': '#4f627d',
  '--font-sans': '"Manrope", "Avenir Next", "Segoe UI", sans-serif',
  '--radius-sm': '8px',
  '--radius-md': '12px',
  '--radius-lg': '20px',
};

export const FONT_OPTIONS = [
  { label: 'Manrope / Avenir Next', value: '"Manrope", "Avenir Next", "Segoe UI", sans-serif' },
  { label: 'IBM Plex Sans', value: '"IBM Plex Sans", "Segoe UI", sans-serif' },
  { label: 'Source Sans 3', value: '"Source Sans 3", "Segoe UI", sans-serif' },
  { label: 'Public Sans', value: '"Public Sans", "Segoe UI", sans-serif' },
  { label: 'Nunito Sans', value: '"Nunito Sans", "Segoe UI", sans-serif' },
];

export const THEME_PRESETS: ThemePreset[] = [
  {
    id: 'default',
    label: 'NextCompli Default',
    overrides: DEFAULT_THEME,
  },
  {
    id: 'atlas',
    label: 'Atlas Blue',
    overrides: {
      '--color-canvas': '#eef4ff',
      '--color-canvas-alt': '#e3ecfb',
      '--color-primary': '#0f62fe',
      '--color-primary-strong': '#0043ce',
      '--color-accent': '#5e45ff',
      '--color-text-primary': '#1b1f28',
      '--color-text-strong': '#123056',
      '--color-text-muted': '#42556f',
    },
  },
  {
    id: 'harbor',
    label: 'Harbor Slate',
    overrides: {
      '--color-canvas': '#f4f7f9',
      '--color-canvas-alt': '#e9eef3',
      '--color-primary': '#1d4ed8',
      '--color-primary-strong': '#1e3a8a',
      '--color-accent': '#7c3aed',
      '--color-success': '#1f7a43',
      '--color-text-primary': '#1f2937',
      '--color-text-strong': '#243b53',
      '--color-text-muted': '#52667d',
    },
  },
];

const shouldHonorStoredBrandOverrides = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }
  if (process.env.NODE_ENV === 'production') {
    return true;
  }
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get(LOCAL_BRAND_OVERRIDE_QUERY_PARAM) === '1';
  } catch {
    return false;
  }
};

const clearLegacyStoredLogo = (): void => {
  if (typeof window === 'undefined') {
    return;
  }
  window.localStorage.removeItem(LEGACY_WEBSITE_LOGO_STORAGE_KEY);
};

const resetStoredBrandOverridesForLocalDev = (): void => {
  if (typeof window === 'undefined' || shouldHonorStoredBrandOverrides()) {
    return;
  }
  window.localStorage.removeItem(THEME_STORAGE_KEY);
  window.localStorage.removeItem(WEBSITE_LOGO_STORAGE_KEY);
};

export const loadThemeOverrides = (): ThemeOverrides => {
  if (!shouldHonorStoredBrandOverrides()) {
    return {};
  }
  try {
    const raw = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw) as ThemeOverrides;
    return typeof parsed === 'object' && parsed ? parsed : {};
  } catch {
    return {};
  }
};

export const applyThemeOverrides = (overrides: ThemeOverrides): void => {
  const root = document.documentElement;
  const merged: Required<ThemeOverrides> = { ...DEFAULT_THEME, ...overrides };
  (Object.keys(merged) as Array<keyof ThemeOverrides>).forEach((token) => {
    const value = merged[token];
    if (value) {
      root.style.setProperty(token, value);
    }
  });
};

export const saveThemeOverrides = (overrides: ThemeOverrides): void => {
  window.localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(overrides));
  applyThemeOverrides(overrides);
  window.dispatchEvent(new CustomEvent('nextcompli:theme-updated'));
};

export const resetThemeOverrides = (): void => {
  window.localStorage.removeItem(THEME_STORAGE_KEY);
  applyThemeOverrides({});
  window.dispatchEvent(new CustomEvent('nextcompli:theme-updated'));
};

export const initializeTheme = (): void => {
  resetStoredBrandOverridesForLocalDev();
  clearLegacyStoredLogo();
  applyThemeOverrides(loadThemeOverrides());
};

export const loadCustomThemePresets = (): ThemePreset[] => {
  try {
    const raw = window.localStorage.getItem(THEME_CUSTOM_PRESETS_STORAGE_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw) as ThemePreset[];
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed
      .filter((item) => item && typeof item.id === 'string' && typeof item.label === 'string' && typeof item.overrides === 'object')
      .map((item) => ({
        id: item.id,
        label: item.label,
        overrides: item.overrides || {},
      }));
  } catch {
    return [];
  }
};

export const saveCustomThemePresets = (presets: ThemePreset[]): void => {
  window.localStorage.setItem(THEME_CUSTOM_PRESETS_STORAGE_KEY, JSON.stringify(presets));
  window.dispatchEvent(new CustomEvent('nextcompli:theme-presets-updated'));
};

export const loadWebsiteLogo = (): string => {
  if (!shouldHonorStoredBrandOverrides()) {
    return DEFAULT_WEBSITE_LOGO;
  }
  try {
    const raw = window.localStorage.getItem(WEBSITE_LOGO_STORAGE_KEY);
    if (!raw || !raw.trim()) {
      return DEFAULT_WEBSITE_LOGO;
    }
    return raw;
  } catch {
    return DEFAULT_WEBSITE_LOGO;
  }
};

export const saveWebsiteLogo = (logoPathOrDataUrl: string): void => {
  const nextValue = logoPathOrDataUrl && logoPathOrDataUrl.trim()
    ? logoPathOrDataUrl.trim()
    : DEFAULT_WEBSITE_LOGO;
  window.localStorage.setItem(WEBSITE_LOGO_STORAGE_KEY, nextValue);
  window.dispatchEvent(new CustomEvent('nextcompli:logo-updated'));
};

export const resetWebsiteLogo = (): void => {
  window.localStorage.removeItem(WEBSITE_LOGO_STORAGE_KEY);
  window.dispatchEvent(new CustomEvent('nextcompli:logo-updated'));
};
